/**
 * proactive-hub.ts — PIL Frontend Intelligence Hub
 * public/js/ai/assistant-modules/proactive-hub.ts
 *
 * Responsibilities:
 *  1. Poll /api/ai/pil/notifications every 60s (visibility-aware, pauses on hidden tab)
 *  2. Render notification cards in the AI drawer (severity-ordered, max 5 visible)
 *  3. Show toast for critical/high signals (quiet-hours-aware via server response)
 *  4. Expose action handlers: startReview(), openChat(), viewMatter(), dismissCard()
 *  5. Receive PIL metadata from chat responses and update drawer state
 *  6. Render proactive chips in the chat input area after a proactive chat turn
 *  7. Record dismissals/feedback to /api/ai/pil/feedback (best-effort, never throws)
 *
 * Instantiated by main.ts:
 *   import { ProactiveHub } from './assistant-modules/proactive-hub';
 *   const proactiveHub = new ProactiveHub({ pollInterval: 60_000 });
 *   await proactiveHub.init();
 *   window.AkiliBrain.proactiveHub = proactiveHub;
 *
 * After a chat response containing PIL metadata:
 *   if (response?.pil?.chips?.length) {
 *     proactiveHub.renderProactiveChips(response.pil.chips);
 *   }
 *
 * HTML prerequisites (in admin.html / advocate.html AI drawer shell):
 *   <div id="pil-hub-container" class="pil-hub" role="region" aria-label="Intelligence alerts"></div>
 *   <div id="pil-chips" class="pil-chips" role="group" aria-label="Suggested actions"></div>
 *
 * Graceful degradation:
 *   All network calls are wrapped in try/catch. PIL never throws to the host app.
 *   If AkiliPortal is unavailable, polling silently halts. Core chat is unaffected.
 */

// ── Interfaces ────────────────────────────────────────────────────────────────

/** Constructor options for {@link ProactiveHub}. */
export interface ProactiveHubOptions {
  /** Polling interval in milliseconds. Default: 60 000 ms (60 s). */
  pollInterval?: number;
  /** Toast auto-dismiss duration in milliseconds. Default: 8 000 ms (8 s). */
  toastDuration?: number;
}

/** Severity level of a PIL notification signal. */
export type PilSeverity = 'critical' | 'high' | 'medium' | 'low' | 'info';

/** Canonical signal type identifiers returned by the PIL backend. */
export type PilSignalType =
  | 'matter_stale'
  | 'deadline_proximity'
  | 'limitation_period'
  | 'invoice_overdue'
  | 'client_inactivity'
  | 'distress_detected'
  | 'contract_uploaded'
  | 'unusual_work_hours'
  | 'burnout_risk'
  | 'retainer_low'
  | 'billing_anomaly'
  | 'war_room_unresolved'
  | 'client_portal_inactive'
  | 'storage_quota_warning'
  | 'trial_expiry'
  | (string & {}); // allow future/unknown signal types without losing autocomplete

/** Available primary action types on a notification card. */
export type PilActionType =
  | 'start_review'
  | 'open_chat'
  | 'view_matter'
  | 'view_document'
  | 'dismiss';

/** Feedback types accepted by /api/ai/pil/feedback. */
export type PilFeedbackType =
  | 'dismissed'
  | 'acted'
  | 'snoozed'
  | 'thumbs_up'
  | 'thumbs_down';

// ── Action payload variants ───────────────────────────────────────────────────

/** Payload for the `open_chat` action type. */
export interface OpenChatPayload {
  prompt?: string;
  autoSend?: boolean;
}

/** Payload for the `view_matter` action type. */
export interface ViewMatterPayload {
  matter_id?: string;
}

/** Payload for the `view_document` action type. */
export interface ViewDocumentPayload {
  document_id?: string;
}

/** Union of all known action payloads, with an escape hatch for unknowns. */
export type PilActionPayload =
  | OpenChatPayload
  | ViewMatterPayload
  | ViewDocumentPayload
  | Record<string, unknown>;

// ── Notification object ───────────────────────────────────────────────────────

/**
 * A single notification object as returned by /api/ai/pil/notifications.
 * The array is server-ordered by priority (critical first).
 */
export interface PilNotification {
  /** Opaque notification UUID — used to key dismiss/feedback calls. */
  id: string;
  /** Parent signal ID (integer FK). Null for system-generated notifications. */
  signal_id?: number | null;
  /** Category of the underlying signal. */
  signal_type: PilSignalType;
  /** Visual/auditory urgency tier. */
  severity: PilSeverity;
  /** Short headline displayed in the card title. */
  title: string;
  /** Body copy displayed beneath the headline. */
  message: string;
  /** Unix epoch seconds at which the notification was created on the server. */
  created_at: number;
  /** Primary CTA type. Absent or `'dismiss'` → no action button rendered. */
  action_type?: PilActionType;
  /** Label for the primary CTA button. Falls back to `'View'` if absent. */
  button_label?: string;
  /** Typed payload forwarded to {@link ProactiveHub.performAction}. */
  action_payload?: PilActionPayload;
}

// ── PIL response metadata (from chat SSE stream) ──────────────────────────────

/** Emotion state object forwarded from the backend PIL pipeline. */
export interface PilEmotion {
  [key: string]: unknown;
}

/**
 * PIL metadata block attached to a chat response's SSE `meta` event.
 * Passed to {@link ProactiveHub.onChatResponse} by the chat handler.
 */
export interface PilMeta {
  /** Whether this response was generated by the proactive engine. */
  proactive: boolean;
  /** Follow-up action chips to display below the chat input. */
  chips: string[];
  /** True if the emotion field was predicted rather than user-declared. */
  emotion_predicted?: boolean;
  /** Structured emotion object for the emotion avatar. Null if not detected. */
  emotion?: PilEmotion | null;
  /** Conversation style hint from the PIL engine. Null if not set. */
  style?: string | null;
}

// ── Internal API response shapes ──────────────────────────────────────────────

/** Shape of the /api/ai/pil/notifications response body. */
interface PilNotificationsResponse {
  notifications?: PilNotification[];
}

/** Shape of the /api/ai/pil/reviews/from-signal/:signalId response body. */
interface ReviewFromSignalResponse {
  already_in_progress?: boolean;
  review_type?: string;
}

// ── WeakMap for delegated click handlers ──────────────────────────────────────
// Avoids polluting HTMLElement with a non-standard `_pilHandler` property.
const _pilHandlerMap = new WeakMap<
  HTMLElement,
  (e: MouseEvent) => void
>();

// ── ProactiveHub class ────────────────────────────────────────────────────────

export class ProactiveHub {
  // ─── Constructor ────────────────────────────────────────────────────────────

  /** Polling interval in ms (default 60s) */
  readonly pollInterval: number;
  /** Toast auto-dismiss duration in ms (default 8s) */
  readonly toastDuration: number;
  /** Current notifications array (server-ordered by priority) */
  notifications: PilNotification[];
  /** ID of the notification currently shown in the toast */
  activeToastId: string | null;
  private _pollTimer: ReturnType<typeof setInterval> | null;
  private _toastTimer: ReturnType<typeof setTimeout> | null;
  private _chipsTimer: ReturnType<typeof setTimeout> | null;
  /** Whether the browser tab is visible */
  private _visible: boolean;
  /** Prevents double-init */
  private _initialized: boolean;
  /** Stored so we can removeEventListener cleanly */
  private readonly _onVisibilityChange: () => void;

  constructor(opts: ProactiveHubOptions = {}) {
    this.pollInterval  = opts.pollInterval  ?? 60_000;
    this.toastDuration = opts.toastDuration ?? 8_000;
    this.notifications = [];
    this.activeToastId = null;
    this._pollTimer    = null;
    this._toastTimer   = null;
    this._chipsTimer   = null;
    this._visible      = !document.hidden;
    this._initialized  = false;

    // Visibility handler stored on instance so we can removeEventListener cleanly
    this._onVisibilityChange = () => {
      this._visible = !document.hidden;
      // Immediate poll on tab focus — catch any signals that fired while hidden
      if (this._visible) this._poll();
    };
  }

  // ─── Lifecycle ──────────────────────────────────────────────────────────────

  /**
   * Initialise the hub: attach listeners, run first poll, start polling loop.
   * Safe to call multiple times — subsequent calls are no-ops.
   */
  async init(): Promise<void> {
    if (this._initialized) return;
    this._initialized = true;

    document.addEventListener('visibilitychange', this._onVisibilityChange);
    await this._poll();
    this._startPolling();
  }

  /**
   * Tear down polling and event listeners. Call on SPA route change or logout.
   */
  destroy(): void {
    this._stopPolling();
    document.removeEventListener('visibilitychange', this._onVisibilityChange);
    const toast = document.getElementById('pil-toast');
    if (toast) toast.remove();
  }

  // ─── Polling ────────────────────────────────────────────────────────────────

  private _startPolling(): void {
    this._pollTimer = setInterval(() => {
      if (this._visible) this._poll();
    }, this.pollInterval);
  }

  private _stopPolling(): void {
    if (this._pollTimer) {
      clearInterval(this._pollTimer);
      this._pollTimer = null;
    }
  }

  private async _poll(): Promise<void> {
    // Guard: AkiliPortal must be available (set by the host portal bootstrap)
    // [FIX] Previously returned silently, leaving the hub container permanently empty and
    // hidden by .pil-hub:empty { display:none }. Now logs a warning so the absence is
    // visible in the console, and injects a mock notification on localhost so the hub
    // UI can be developed and tested without the full portal bootstrap.
    if (typeof AkiliPortal === 'undefined') {
      console.warn('[PIL] AkiliPortal not found — PIL polling disabled. Ensure api-client.js loads before this module.');
      if (typeof window !== 'undefined' && window.location.hostname === 'localhost') {
        this.notifications = [{
          id:             'dev-mock-1',
          signal_type:    'matter_stale',
          severity:       'high',
          title:          'Dev Mock: Stale Matter',
          message:        'Matter #TEST-001 has had no activity in 45 days. Consider updating the client.',
          created_at:     Math.floor(Date.now() / 1000),
          action_type:    'view_matter',
          button_label:   'Review Matter',
          action_payload: { matter_id: 'TEST-001' },
        } as PilNotification];
        this._render();
      }
      return;
    }

    try {
      const data = await AkiliPortal.apiFetch('/api/ai/pil/notifications') as unknown as PilNotificationsResponse;
      this.notifications = data.notifications ?? [];
      this._render();
      this._checkForCritical();
    } catch (_) {
      // PIL never degrades the core experience — silently swallow network errors
    }
  }

  // ─── Rendering ──────────────────────────────────────────────────────────────

  /**
   * Re-render the entire hub container from this.notifications.
   * Clears the container on empty state; adds .has-alerts class when populated.
   */
  private _render(): void {
    const container = document.getElementById('pil-hub-container');
    if (!container) return;

    if (this.notifications.length === 0) {
      container.innerHTML = '';
      container.classList.remove('has-alerts');
      this._updateBadge(0);
      return;
    }

    container.classList.add('has-alerts');
    // Render header + up to 5 cards (remaining are loaded on next poll or manual refresh)
    container.innerHTML = `
      <div class="pil-hub-header">
        <span class="pil-hub-icon" aria-hidden="true">✦</span>
        <span class="pil-hub-title">Intelligence Alerts</span>
        <span class="pil-badge" aria-label="${this.notifications.length} alerts">${this.notifications.length}</span>
        <button class="pil-dismiss-all btn-ghost-sm" data-pil-action="dismiss-all" aria-label="Clear all alerts">
          Clear all
        </button>
      </div>
      <div class="pil-card-list" role="list">
        ${this.notifications.slice(0, 5).map(n => this._renderCard(n)).join('')}
      </div>
    `;

    this._updateBadge(this.notifications.length);
    this._bindCardEvents(container as HTMLElement);
  }

  /**
   * Renders a single notification card as an HTML string.
   * @param n  Notification object from /api/ai/pil/notifications
   */
  private _renderCard(n: PilNotification): string {
    const timeAgo      = this._formatTimeAgo(n.created_at);
    const severityIcon = this._severityIcon(n.severity);

    const actionBtn = n.action_type && n.action_type !== 'dismiss'
      ? `<button
           class="btn-primary-sm pil-action-btn"
           data-pil-action="act"
           data-notif-id="${this._escapeAttr(n.id)}"
           data-signal-id="${n.signal_id ?? ''}"
           data-action-type="${this._escapeAttr(n.action_type)}"
           data-action-payload='${this._escapeAttr(JSON.stringify(n.action_payload ?? {}))}'
           aria-label="${this._escapeAttr(n.button_label || 'View')}"
         >${this._escapeHtml(n.button_label || 'View')}</button>`
      : '';

    return `
      <div
        class="pil-card severity-${this._escapeAttr(n.severity ?? 'medium')}"
        data-notif-id="${this._escapeAttr(n.id)}"
        data-signal-id="${n.signal_id ?? ''}"
        role="listitem"
      >
        <div class="pil-card-meta">
          <span class="pil-signal-type" title="${this._escapeAttr(n.signal_type)}">
            ${severityIcon} ${this._escapeHtml(this._formatSignalType(n.signal_type))}
          </span>
          <span class="pil-time" aria-label="Received ${timeAgo}">${timeAgo}</span>
          <button
            class="pil-card-dismiss btn-icon"
            data-pil-action="dismiss"
            data-notif-id="${this._escapeAttr(n.id)}"
            data-signal-id="${n.signal_id ?? ''}"
            title="Dismiss"
            aria-label="Dismiss this alert"
          >✕</button>
        </div>
        <p class="pil-card-title">${this._escapeHtml(n.title)}</p>
        <p class="pil-card-body">${this._escapeHtml(n.message)}</p>
        ${actionBtn ? `<div class="pil-card-actions">${actionBtn}</div>` : ''}
      </div>
    `;
  }

  /**
   * Delegate click events on [data-pil-action] elements within the container.
   * Using event delegation avoids re-binding on every re-render.
   */
  private _bindCardEvents(container: HTMLElement): void {
    // Remove any existing delegated listener (avoid duplicate handlers on re-render)
    const existing = _pilHandlerMap.get(container);
    if (existing) {
      container.removeEventListener('click', existing as EventListener);
    }

    const handler = (e: MouseEvent) => {
      const el = (e.target as HTMLElement | null)?.closest<HTMLElement>('[data-pil-action]');
      if (!el) return;

      e.stopPropagation();

      const action     = el.dataset.pilAction;
      const notifId    = el.dataset.notifId ?? '';
      const signalId   = el.dataset.signalId ? parseInt(el.dataset.signalId, 10) : null;
      const actionType = (el.dataset.actionType ?? '') as PilActionType;
      let payload: PilActionPayload = {};
      try {
        if (el.dataset.actionPayload) payload = JSON.parse(el.dataset.actionPayload) as PilActionPayload;
      } catch { /* ignore parse errors */ }

      switch (action) {
        case 'dismiss':
          this.dismissCard(notifId, signalId);
          break;
        case 'dismiss-all':
          this.dismissAll();
          break;
        case 'act':
          this.performAction(notifId, signalId, actionType, payload);
          break;
      }
    };

    _pilHandlerMap.set(container, handler);
    container.addEventListener('click', handler as EventListener);
  }

  // ─── Toasts (critical/high severity signals only) ────────────────────────────

  /**
   * Checks the notification list for critical-severity items and shows a toast
   * for the highest-priority one not yet displayed.
   */
  private _checkForCritical(): void {
    // Show toast only for critical severity (high severity surfaced in cards only)
    const critical = this.notifications.find(n => n.severity === 'critical');
    if (!critical) return;
    if (this.activeToastId === critical.id) return; // already shown this toast

    this._showToast(critical);
  }

  /**
   * Renders and animates a toast notification for the given notification object.
   * Auto-dismisses after this.toastDuration milliseconds.
   */
  private _showToast(notification: PilNotification): void {
    this.activeToastId = notification.id;

    // Remove any existing toast before creating a new one
    const existing = document.getElementById('pil-toast');
    if (existing) existing.remove();

    const toast = document.createElement('div');
    toast.id        = 'pil-toast';
    toast.className = `pil-toast severity-${notification.severity ?? 'high'}`;
    toast.setAttribute('role', 'alert');
    toast.setAttribute('aria-live', 'assertive');
    toast.innerHTML = `
      <div class="pil-toast-inner">
        <span class="pil-toast-icon" aria-hidden="true">${this._severityIcon(notification.severity)}</span>
        <div class="pil-toast-content">
          <p class="pil-toast-title">${this._escapeHtml(notification.title)}</p>
          <p class="pil-toast-body">${this._escapeHtml(notification.message)}</p>
        </div>
        <div class="pil-toast-actions">
          ${notification.action_type && notification.action_type !== 'dismiss'
            ? `<button class="btn-primary-sm" id="pil-toast-act">${this._escapeHtml(notification.button_label || 'View')}</button>`
            : ''}
          <button class="btn-ghost-sm" id="pil-toast-dismiss" aria-label="Dismiss toast">×</button>
        </div>
      </div>
    `;

    document.body.appendChild(toast);

    // Animate in on next frame (allows CSS transition to trigger)
    requestAnimationFrame(() => {
      requestAnimationFrame(() => toast.classList.add('visible'));
    });

    // Action button
    document.getElementById('pil-toast-act')?.addEventListener('click', () => {
      this.performAction(
        notification.id,
        notification.signal_id ?? null,
        notification.action_type as PilActionType,
        notification.action_payload ?? {},
      );
      this._dismissToast(toast);
    });

    // Dismiss button
    document.getElementById('pil-toast-dismiss')?.addEventListener('click', () => {
      this.dismissCard(notification.id, notification.signal_id ?? null);
      this._dismissToast(toast);
    });

    // Auto-dismiss
    this._toastTimer = setTimeout(() => this._dismissToast(toast), this.toastDuration);
  }

  /**
   * Animate out and remove the toast element.
   */
  private _dismissToast(toast: HTMLElement): void {
    if (this._toastTimer) {
      clearTimeout(this._toastTimer);
      this._toastTimer = null;
    }
    if (!toast || !toast.isConnected) return;
    toast.classList.remove('visible');
    setTimeout(() => { if (toast.isConnected) toast.remove(); }, 400);
  }

  // ─── Actions ─────────────────────────────────────────────────────────────────

  /**
   * Executes the primary action of a notification card.
   * Records 'acted' feedback, then routes to the appropriate handler.
   */
  async performAction(
    notifId: string,
    signalId: number | null,
    actionType: PilActionType,
    payload: PilActionPayload,
  ): Promise<void> {
    // Record feedback asynchronously — never block the action
    this._recordFeedback(notifId, signalId, 'acted');

    switch (actionType) {
      case 'start_review': {
        if (!signalId) break;
        try {
          const result = await (AkiliPortal as NonNullable<typeof AkiliPortal>).apiFetch(
            `/api/ai/pil/reviews/from-signal/${signalId}`,
            { method: 'POST' },
          ) as unknown as ReviewFromSignalResponse;
          if (result.already_in_progress) {
            this._showInlineMessage('A review is already in progress for this document.');
          } else {
            this._showInlineMessage(
              `Review started (${result.review_type ?? 'compliance check'}). I'll notify you when it's complete.`,
            );
          }
        } catch {
          this._showInlineMessage('Could not start review — please try from the document panel.');
        }
        break;
      }

      case 'open_chat': {
        // Dispatch event that handlers.ts / chat module listens for
        const chatPayload = payload as OpenChatPayload;
        document.dispatchEvent(new CustomEvent('akili:open-chat', {
          detail: {
            prompt:   chatPayload.prompt   ?? '',
            autoSend: chatPayload.autoSend ?? false,
          },
          bubbles: true,
        }));
        break;
      }

      case 'view_matter': {
        const matterPayload = payload as ViewMatterPayload;
        if (matterPayload.matter_id && typeof window.AkiliPortalRouter !== 'undefined') {
          window.AkiliPortalRouter.navigate('matters', matterPayload.matter_id);
        } else if (matterPayload.matter_id) {
          // Fallback: dispatch a navigation event for the host app to handle
          document.dispatchEvent(new CustomEvent('akili:navigate', {
            detail: { section: 'matters', id: matterPayload.matter_id },
            bubbles: true,
          }));
        }
        break;
      }

      case 'view_document': {
        const docPayload = payload as ViewDocumentPayload;
        if (docPayload.document_id) {
          document.dispatchEvent(new CustomEvent('akili:navigate', {
            detail: { section: 'documents', id: docPayload.document_id },
            bubbles: true,
          }));
        }
        break;
      }

      default:
        break;
    }

    // Remove the card from the UI after acting on it
    this._removeCardFromUI(notifId);
  }

  /**
   * Dismisses a single notification card (no action taken).
   */
  async dismissCard(notifId: string, signalId: number | null): Promise<void> {
    this._recordFeedback(notifId, signalId, 'dismissed');
    this._removeCardFromUI(notifId);
  }

  /**
   * Dismisses all visible notification cards at once.
   */
  dismissAll(): void {
    const toRecord = this.notifications.map(n => ({ notifId: n.id, signalId: n.signal_id ?? null }));
    // Record all dismissals in parallel — best-effort, do not await
    toRecord.forEach(({ notifId, signalId }) =>
      this._recordFeedback(notifId, signalId, 'dismissed'),
    );
    this.notifications = [];
    this._render();
  }

  // ─── Proactive chips ──────────────────────────────────────────────────────────

  /**
   * Renders proactive action chips below the chat input.
   * Chips are injected by main.ts after a chat response that contains pil.chips.
   * Clicking a chip fires 'akili:open-chat' with autoSend:true and clears the chips.
   * @param chips  Array of chip label strings
   */
  renderProactiveChips(chips: string[]): void {
    const container = document.getElementById('pil-chips');
    if (!container || !chips?.length) return;

    container.innerHTML = chips.map(chip => `
      <button
        class="pil-chip"
        data-prompt="${this._escapeAttr(chip)}"
        aria-label="Send: ${this._escapeAttr(chip)}"
      >${this._escapeHtml(chip)}</button>
    `).join('');

    container.querySelectorAll<HTMLButtonElement>('.pil-chip').forEach(btn => {
      btn.addEventListener('click', () => {
        const prompt = btn.dataset.prompt ?? '';
        document.dispatchEvent(new CustomEvent('akili:open-chat', {
          detail: { prompt, autoSend: true },
          bubbles: true,
        }));
        // Clear chips after click — one-shot usage
        container.innerHTML = '';
      });
    });

    // Auto-clear chips after 90s if not clicked
    if (this._chipsTimer) clearTimeout(this._chipsTimer);
    this._chipsTimer = setTimeout(() => {
      if (container) container.innerHTML = '';
    }, 90_000);
  }

  /**
   * Clears the proactive chips container programmatically.
   */
  clearChips(): void {
    const container = document.getElementById('pil-chips');
    if (container) container.innerHTML = '';
    if (this._chipsTimer) {
      clearTimeout(this._chipsTimer);
      this._chipsTimer = null;
    }
  }

  // ─── External update (called by chat handler after PIL response) ─────────────

  /**
   * Called by the chat handler (via the `akili:pil-response` custom event in main.ts)
   * after receiving a chat response with PIL metadata.
   * Updates emotion state display if present, renders chips if provided.
   */
  onChatResponse(pilMeta: PilMeta): void {
    if (!pilMeta) return;
    if (pilMeta.chips?.length) {
      this.renderProactiveChips(pilMeta.chips);
    }
    // Dispatch event for emotion avatar to update its display
    if (pilMeta.emotion) {
      document.dispatchEvent(new CustomEvent('akili:emotion-update', {
        detail: { emotion: pilMeta.emotion, predicted: pilMeta.emotion_predicted ?? false },
        bubbles: true,
      }));
    }
  }

  // ─── Internal helpers ─────────────────────────────────────────────────────────

  /**
   * Posts feedback to /api/ai/pil/feedback. Best-effort — never throws.
   */
  private async _recordFeedback(
    notifId: string,
    signalId: number | null,
    feedbackType: PilFeedbackType,
  ): Promise<void> {
    if (typeof AkiliPortal === 'undefined') return;
    try {
      await AkiliPortal.apiFetch('/api/ai/pil/feedback', {
        method: 'POST',
        body: JSON.stringify({
          notification_id: notifId  || undefined,
          signal_id:       signalId || undefined,
          feedback_type:   feedbackType,
        }),
      });
    } catch (_) {
      // Feedback is non-critical — silently swallow
    }
  }

  /**
   * Removes a notification card from this.notifications and animates it out of the DOM.
   * Triggers a re-render so the count badge stays accurate.
   */
  private _removeCardFromUI(notifId: string): void {
    this.notifications = this.notifications.filter(n => n.id !== notifId);

    const card = document.querySelector<HTMLElement>(
      `.pil-card[data-notif-id="${CSS.escape(String(notifId))}"]`,
    );
    if (card) {
      card.style.transition   = 'opacity 0.2s ease, transform 0.2s ease, max-height 0.25s ease';
      card.style.opacity      = '0';
      card.style.transform    = 'translateX(12px)';
      card.style.maxHeight    = card.scrollHeight + 'px';
      // Collapse height after opacity fade
      setTimeout(() => {
        card.style.maxHeight    = '0';
        card.style.padding      = '0';
        card.style.marginBottom = '0';
      }, 200);
      setTimeout(() => {
        card.remove();
        this._render(); // full re-render after remove to update header count
      }, 450);
    } else {
      // Card not in DOM (may have been in a toast) — just re-render
      this._render();
    }
  }

  /**
   * Updates the FAB badge count. Badge is hidden when count is 0.
   */
  private _updateBadge(count: number): void {
    // Update FAB badge (external to the drawer, e.g. on the Kili button)
    document.querySelectorAll<HTMLElement>('.pil-fab-badge').forEach(badge => {
      badge.textContent   = count > 0 ? String(count > 99 ? '99+' : count) : '';
      badge.style.display = count > 0 ? 'flex' : 'none';
    });
    // Also update the in-drawer badge if it exists independently
    const drawerBadge = document.querySelector<HTMLElement>('.kili-notif-count');
    if (drawerBadge) {
      drawerBadge.textContent   = count > 0 ? String(count) : '';
      drawerBadge.style.display = count > 0 ? 'inline-flex' : 'none';
    }
  }

  /**
   * Dispatches an inline system message event for the chat panel to display.
   */
  private _showInlineMessage(text: string): void {
    document.dispatchEvent(new CustomEvent('akili:system-message', {
      detail: { text },
      bubbles: true,
    }));
  }

  /**
   * Maps signal_type to a human-readable label.
   */
  private _formatSignalType(type: PilSignalType): string {
    const LABELS: Partial<Record<string, string>> = {
      matter_stale:           'Stale Matter',
      deadline_proximity:     'Deadline',
      limitation_period:      'Limitation Period',
      invoice_overdue:        'Overdue Invoice',
      client_inactivity:      'Client Check-in',
      distress_detected:      'Distress Alert',
      contract_uploaded:      'Document Review',
      unusual_work_hours:     'Late Night',
      burnout_risk:           'Wellness Alert',
      retainer_low:           'Retainer Low',
      billing_anomaly:        'Unbilled Hours',
      war_room_unresolved:    'War Room',
      client_portal_inactive: 'Client Portal',
      storage_quota_warning:  'Storage',
      trial_expiry:           'Trial Expiry',
    };
    return LABELS[type] ?? type.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
  }

  /**
   * Returns a severity emoji/icon character for display.
   */
  private _severityIcon(severity: PilSeverity | undefined): string {
    const ICONS: Record<string, string> = {
      critical: '🔴',
      high:     '🟡',
      medium:   '🔵',
      low:      '⚪',
      info:     '💠',
    };
    return ICONS[severity ?? ''] ?? '⚫';
  }

  /**
   * Formats a Unix timestamp as a relative "time ago" string.
   * @param ts  Unix epoch seconds
   */
  private _formatTimeAgo(ts: number): string {
    if (!ts) return '';
    const diff = Math.floor(Date.now() / 1000) - ts;
    if (diff < 60)    return 'just now';
    if (diff < 3600)  return `${Math.floor(diff / 60)}m ago`;
    if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
    return `${Math.floor(diff / 86400)}d ago`;
  }

  /**
   * Escapes a string for safe insertion as HTML text content.
   */
  private _escapeHtml(str: string | null | undefined): string {
    if (str == null) return '';
    return String(str)
      .replace(/&/g,  '&amp;')
      .replace(/</g,  '&lt;')
      .replace(/>/g,  '&gt;')
      .replace(/"/g,  '&quot;')
      .replace(/'/g,  '&#039;');
  }

  /**
   * Escapes a string for safe insertion as an HTML attribute value.
   * Converts single quotes to prevent attribute injection.
   */
  private _escapeAttr(str: string | null | undefined): string {
    if (str == null) return '';
    return String(str)
      .replace(/&/g,  '&amp;')
      .replace(/"/g,  '&quot;')
      .replace(/'/g,  '&#x27;')
      .replace(/</g,  '&lt;')
      .replace(/>/g,  '&gt;');
  }
}
